import React, { useState, useEffect } from 'react';
import { DataManager } from '../../services/dataManager';
import BaselineQuestionnaire from './BaselineQuestionnaire';

const ProfileModuli = ({ client }) => {
    const [view, setView] = useState('menu'); // menu, new, list, view, anagrafica
    const [assessments, setAssessments] = useState([]);
    const [selectedAssessment, setSelectedAssessment] = useState(null);
    const [loading, setLoading] = useState(false);
    const [selectedDocId, setSelectedDocId] = useState(null);
    const [selectedTool, setSelectedTool] = useState(null);

    // Form data for Anagrafica (Moved to top level to avoid Hook Violation)
    const [formData, setFormData] = useState({
        name: client.name || '',
        phone: client.phone || '',
        birthDate: client.birthDate || ''
    });
    const [isSaving, setIsSaving] = useState(false);

    // Sync formData with client prop when it changes or when entering anagrafica view
    useEffect(() => {
        if (view === 'anagrafica') {
            setFormData({
                name: client.name || '',
                phone: client.phone || '',
                birthDate: client.birthDate || ''
            });
        }
    }, [client.id, view, client.name, client.phone, client.birthDate]);

    useEffect(() => {
        loadAssessments();
    }, [client.id, view]);

    const loadAssessments = async () => {
        setLoading(true);
        const data = await DataManager.getAssessmentsByClient(client.id);
        const baselines = data.filter(a => a.type && a.type.startsWith('baseline_'))
                              .sort((a,b) => new Date(b.timestamp) - new Date(a.timestamp));
        setAssessments(baselines);
        setLoading(false);
    };

    const handleDelete = async (id) => {
        if(window.confirm('Sicuro di voler cancellare questo questionario? I dati non potranno essere recuperati.')) {
            await DataManager.deleteAssessment(id);
            setAssessments(assessments.filter(a => a.id !== id));
            if(selectedAssessment && selectedAssessment.id === id) setView('list');
        }
    };

    if (view === 'new') {
        return (
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm mt-4 relative animate-fade-in">
                <button onClick={() => setView('menu')} className="absolute top-4 right-6 text-sm font-bold text-slate-500 hover:text-slate-800">Torna al menu ✕</button>
                <h3 className="text-xl font-bold text-slate-800 mb-4 border-b pb-2">Scrivi un nuovo Questionario Iniziale</h3>
                <p className="text-sm text-slate-500 mb-6 max-w-2xl">
                    Se senti che le tue priorità, aspettative o motivazioni sono cambiate rispetto all'inizio, compila un nuovo "Questionario Iniziale".
                </p>
                <div className="bg-slate-50 p-6 rounded-2xl border border-emerald-100">
                    <BaselineQuestionnaire
                        clientId={client.id}
                        onComplete={() => {
                            alert('✅ Questionario inviato e aggiornato con successo!');
                            setView('menu');
                        }}
                    />
                </div>
            </div>
        );
    }

    if (view === 'list') {
        return (
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm mt-4 relative animate-fade-in">
                <button onClick={() => setView('menu')} className="absolute top-4 right-6 text-sm font-bold text-slate-500 hover:text-slate-800">Torna al menu ✕</button>
                <h3 className="text-xl font-bold text-slate-800 mb-4 border-b pb-2">I miei Questionari</h3>
                {loading ? <p className="text-slate-500">Caricamento in corso...</p> : (
                    <div className="space-y-4">
                        {assessments.length === 0 ? <p className="text-slate-500">Nessun questionario trovato.</p> : null}
                        {assessments.map(ass => (
                            <div key={ass.id} className="p-4 border border-slate-200 rounded-xl flex justify-between items-center hover:bg-slate-50 transition">
                                <div>
                                    <p className="font-bold text-slate-800">{ass.baselineType || 'Questionario'}</p>
                                    <p className="text-xs text-slate-500">{new Date(ass.timestamp).toLocaleDateString('it-IT', {year: 'numeric', month: 'long', day: 'numeric'})}</p>
                                </div>
                                <div className="flex gap-2">
                                    <button onClick={() => { setSelectedAssessment(ass); setView('view'); }} className="px-3 py-1 bg-emerald-50 text-emerald-700 rounded-lg text-sm font-bold hover:bg-emerald-100">Visualizza / Modifica</button>
                                    <button onClick={() => handleDelete(ass.id)} className="px-3 py-1 bg-red-50 text-red-600 rounded-lg text-sm font-bold hover:bg-red-100">Elimina</button>
                                </div>
                            </div>
                        ))}
                    </div>
                )}
            </div>
        );
    }

    if (view === 'view' && selectedAssessment) {
        const ans = selectedAssessment.qualitative || {};
        const wheel = selectedAssessment.wheelOfLife || {};
        return (
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm mt-4 relative animate-fade-in">
                <div className="flex gap-4 absolute top-4 right-6">
                    <button onClick={() => setView('list')} className="text-sm font-bold text-slate-500 hover:text-slate-800">Indietro</button>
                    <button onClick={() => setView('menu')} className="text-sm font-bold text-slate-500 hover:text-slate-800">Menu ✕</button>
                </div>
                <h3 className="text-xl font-bold text-slate-800 mb-2 border-b pb-2">Dettaglio Questionario {selectedAssessment.baselineType || 'T0'}</h3>
                <p className="text-sm text-slate-500 mb-6">Data compilazione: {new Date(selectedAssessment.timestamp).toLocaleDateString('it-IT')}</p>
                
                <div className="space-y-6">
                    <div>
                        <h4 className="font-bold text-slate-700 mb-2">Risposte Qualitative</h4>
                        <div className="grid md:grid-cols-2 gap-4">
                            <div className="bg-slate-50 p-4 rounded-xl"><strong className="block text-xs text-slate-500">Motivazione</strong><span className="text-sm">{ans.motivazione || 'N/A'}</span></div>
                            <div className="bg-slate-50 p-4 rounded-xl"><strong className="block text-xs text-slate-500">Aspettative</strong><span className="text-sm">{ans.aspettative || 'N/A'}</span></div>
                            <div className="bg-slate-50 p-4 rounded-xl"><strong className="block text-xs text-slate-500">Difficoltà</strong><span className="text-sm">{ans.difficolta || 'N/A'}</span></div>
                            <div className="bg-slate-50 p-4 rounded-xl"><strong className="block text-xs text-slate-500">Risorse</strong><span className="text-sm">{ans.risorse || 'N/A'}</span></div>
                        </div>
                    </div>
                    <div>
                        <h4 className="font-bold text-slate-700 mb-2">Ruota della Vita (Score 1-10)</h4>
                        <div className="flex flex-wrap gap-2">
                             {Object.entries(wheel).map(([k,v]) => (
                                 <span key={k} className="px-3 py-1 bg-indigo-50 text-indigo-700 rounded-full text-sm font-semibold">{k}: {v}</span>
                             ))}
                        </div>
                    </div>
                </div>

                <div className="mt-8 flex gap-3 border-t pt-4">
                    <p className="text-xs text-gray-500 w-full mb-2">Per modificare le risposte esistenti in modo strutturato, chiedi supporto al tuo counselor in incontro oppure compila un "Nuovo Questionario" dal menu per aggiornare la tua situazione odierna.</p>
                </div>
            </div>
        );
    }

    if (view === 'documents') {
        const docs = [
            { id: 'privacy', title: 'Gestione Dati & Sicurezza', icon: '🔒', excerpt: 'Trasparenza su come trattiamo e proteggiamo i tuoi dati sensibili.', status: 'active' },
            { id: 'counseling', title: 'Cos\'è il Counseling?', icon: '🌿', excerpt: 'Una guida per comprendere il percorso di consapevolezza e aiuto.', status: 'active' },
            { id: 'pact', title: 'Patto Relazionale', icon: '🤝', excerpt: 'Le basi della nostra collaborazione e del reciproco impegno.', status: 'placeholder' }
        ];

        if (selectedDocId === 'privacy') {
            return (
                <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-lg mt-4 relative animate-fade-in max-h-[80vh] overflow-y-auto">
                    <button onClick={() => setSelectedDocId(null)} className="absolute top-4 right-6 text-sm font-bold text-slate-500 hover:text-slate-800">← Indietro</button>
                    
                    <div className="prose prose-slate max-w-none">
                        <div className="flex items-center gap-4 mb-8 border-b pb-4 mt-4">
                             <div className="w-16 h-16 bg-blue-100 rounded-2xl flex items-center justify-center text-3xl shadow-sm">🔒</div>
                             <div>
                                <h2 className="text-3xl font-black text-slate-800 m-0">Gestione Dati & Sicurezza</h2>
                                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Trasparenza e Protezione (GDPR)</p>
                             </div>
                        </div>

                        <section className="mb-10">
                            <h3 className="text-xl font-bold text-slate-800 mb-4">1. Chi gestisce i tuoi dati?</h3>
                            <p className="text-slate-700 leading-relaxed mb-4">
                                I tuoi dati sono sotto la responsabilità del tuo <strong>Counselor Professionista</strong>. Questa sezione ti spiega tecnicamente come vengono gestiti all'interno della piattaforma <strong>AwarenessTool</strong>, in conformità con l'informativa cartacea/digitale che hai sottoscritto all'inizio del percorso.
                            </p>
                        </section>

                        <section className="mb-10">
                            <h3 className="text-xl font-bold text-slate-800 mb-4">2. Tipologia dei Dati Trattati</h3>
                            <p className="text-slate-700 leading-relaxed mb-4">La piattaforma gestisce due tipologie di dati:</p>
                            <ul className="space-y-3 list-none p-0">
                                <li className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <span className="w-8 h-8 bg-white shadow-sm rounded-lg flex items-center justify-center">🆔</span>
                                    <div>
                                        <strong className="text-slate-800 block mb-1">Dati Anagrafici e di Contatti</strong>
                                        <p className="text-xs text-slate-500">Nome, Cognome, Email, Numero di telefono, Data di nascita.</p>
                                    </div>
                                </li>
                                <li className="flex items-start gap-4 p-4 bg-slate-50 rounded-xl border border-slate-100">
                                    <span className="w-8 h-8 bg-white shadow-sm rounded-lg flex items-center justify-center">🧠</span>
                                    <div>
                                        <strong className="text-slate-800 block mb-1">Dati "Particolari" (Sensibili)</strong>
                                        <p className="text-xs text-slate-500">Informazioni sul benessere psico-fisico, schemi di vita, risposte qualitative ai questionari e note nel diario di bordo.</p>
                                    </div>
                                </li>
                            </ul>
                        </section>

                        <section className="mb-10">
                            <h3 className="text-xl font-bold text-slate-800 mb-4">3. Infrastruttura Tecnologica e Sicurezza</h3>
                            <p className="text-slate-700 leading-relaxed mb-4">
                                I tuoi dati sono gestiti tramite l'infrastruttura <strong>Google Firebase / Firestore</strong>, un servizio cloud ad alta affidabilità certificato GDPR.
                            </p>
                            <div className="grid md:grid-cols-2 gap-4 mt-6">
                                <div className="p-5 bg-indigo-50 rounded-2xl border border-indigo-100">
                                    <h4 className="font-bold text-indigo-800 text-sm mb-2 uppercase tracking-wide">Archiviazione Sicura</h4>
                                    <p className="text-xs text-indigo-700 leading-relaxed">I server sono situati all'interno della <strong>Unione Europea</strong> (Solitamente Frankfurt/Belgium) per garantire il massimo livello di protezione legale.</p>
                                </div>
                                <div className="p-5 bg-indigo-50 rounded-2xl border border-indigo-100">
                                    <h4 className="font-bold text-indigo-800 text-sm mb-2 uppercase tracking-wide">Accesso Restrittivo</h4>
                                    <p className="text-xs text-indigo-700 leading-relaxed">L'accesso è regolato da <strong>Security Rules</strong> a livello di database: solo tu e il tuo counselor potete visualizzare i dati sensibili legati al tuo profilo.</p>
                                </div>
                            </div>
                        </section>

                        <section className="mb-10">
                            <h3 className="text-xl font-bold text-slate-800 mb-4">4. Diritti dell'Interessato</h3>
                            <p className="text-slate-700 leading-relaxed mb-4">
                                Ai sensi del GDPR, hai il diritto di:
                            </p>
                            <ul className="text-sm text-slate-600 space-y-2 list-disc list-inside">
                                <li><strong>Accedere</strong> ai tuoi dati in qualsiasi momento.</li>
                                <li>Richiedere la <strong>Rettifica</strong> o l'<strong>Aggiornamento</strong> (puoi farlo anche autonomamente nella tua area profilo).</li>
                                <li>Richiedere la <strong>Cancellazione</strong> dei tuoi dati (Diritto all'oblio).</li>
                                <li>Richiedere la <strong>Portabilità</strong> in formato digitale leggibile.</li>
                            </ul>
                            <p className="text-xs text-slate-400 mt-6 pt-4 border-t border-slate-100 italic">
                                Per esercitare questi diritti, puoi contattare il tuo Counselor all'indirizzo email associato al tuo profilo AwarenessTool.
                            </p>
                        </section>
                    </div>
                </div>
            );
        }

        if (selectedDocId === 'counseling') {
            return (
                <div className="bg-white rounded-2xl p-8 border border-slate-100 shadow-lg mt-4 relative animate-fade-in max-h-[80vh] overflow-y-auto">
                    <button onClick={() => setSelectedDocId(null)} className="absolute top-4 right-6 text-sm font-bold text-slate-500 hover:text-slate-800">← Indietro</button>
                    
                    <div className="prose prose-slate max-w-none">
                        <div className="flex items-center gap-4 mb-8 border-b pb-4 mt-4">
                             <div className="w-16 h-16 bg-emerald-100 rounded-2xl flex items-center justify-center text-3xl shadow-sm">🌿</div>
                             <div>
                                <h2 className="text-3xl font-black text-slate-800 m-0">Cos'è il Counseling?</h2>
                                <p className="text-xs text-slate-400 font-bold uppercase tracking-widest mt-1">Guida al percorso di consapevolezza</p>
                             </div>
                        </div>

                        <section className="mb-10">
                            <h3 className="text-xl font-bold text-[#24485d] mb-4 flex items-center gap-2 italic">
                                <span className="bg-[#335d73] text-white w-6 h-6 rounded-full flex items-center justify-center text-[10px] not-italic">1</span>
                                Definizione e Ambito
                            </h3>
                            <p className="text-slate-700 leading-relaxed mb-4">
                                Il counseling si configura come una <strong>relazione d'aiuto professionale</strong>, finalizzata a sostenere il cliente nella risoluzione di un problema specifico, nella chiarificazione di un disagio esistenziale o nel potenziamento della propria consapevolezza.
                            </p>
                            <p className="text-slate-700 leading-relaxed mb-4 font-medium italic text-[#24485d] bg-[#f0f5f8] p-4 rounded-xl border border-[#d9e7ed]">
                                Il metodo <strong>BioGestalt®</strong> è un approccio integrato di counseling nato in Italia che unisce la <strong>Terapia della Gestalt</strong> (focalizzata sul "qui e ora" e la consapevolezza emotiva) e la <strong>Bioenergetica</strong> (centrata sul corpo e lo sblocco delle tensioni muscolari). Mira a riarmonizzare mente e corpo, migliorando la vitalità e le relazioni.
                            </p>
                            <p className="text-slate-700 leading-relaxed mb-4">
                                A differenza della psicoterapia, il counseling non interviene su patologie psichiche. Il suo obiettivo primario è aiutare l'individuo ad <strong>armonizzare la propria personalità</strong>, mettendola al servizio dei propri obiettivi e della propria felicità. Infine, il counselor lavora spesso in <strong>equipe multidisciplinari</strong> per supportare persone che necessitano di un sostegno integrato su diverse sfere della propria vita.
                            </p>
                        </section>

                        <section className="mb-10">
                             <h3 className="text-xl font-bold text-[#24485d] mb-6 flex items-center gap-2 italic">
                                <span className="bg-[#335d73] text-white w-6 h-6 rounded-full flex items-center justify-center text-[10px] not-italic">2</span>
                                Approcci Teorici Fondamentali
                             </h3>
                             <div className="space-y-6">
                                 <div className="p-5 rounded-2xl border border-slate-100 bg-white shadow-sm hover:border-indigo-100 transition">
                                     <div className="flex items-center gap-3 mb-2">
                                         <div className="w-8 h-8 bg-blue-100 rounded-lg flex items-center justify-center text-lg">👤</div>
                                         <h4 className="font-bold text-slate-800">Terapia Centrata sul Cliente (C. Rogers)</h4>
                                     </div>
                                     <p className="text-sm text-slate-600 leading-relaxed mb-2">
                                        L'approccio rogersiano è il fondamento del counseling. Si basa sulla convinzione che il cliente possieda già in sé le risorse per il cambiamento.
                                     </p>
                                     <ul className="text-xs text-slate-500 space-y-1 list-disc list-inside bg-blue-50/50 p-3 rounded-xl border border-blue-50">
                                         <li><strong>Empatia:</strong> La capacità di sentire il mondo interiore del cliente "come se" fosse il proprio.</li>
                                         <li><strong>Accettazione Incondizionata:</strong> Accogliere la persona per come è, senza condizioni o giudizi morali.</li>
                                         <li><strong>Congruenza:</strong> L'autenticità del counselor come essere umano presente nella relazione.</li>
                                     </ul>
                                 </div>
                                 <div className="p-5 rounded-2xl border border-slate-100 bg-white shadow-sm hover:border-indigo-100 transition">
                                     <div className="flex items-center gap-3 mb-2">
                                         <div className="w-8 h-8 bg-purple-100 rounded-lg flex items-center justify-center text-lg">👁️</div>
                                         <h4 className="font-bold text-slate-800">La Gestalt (F. Perls)</h4>
                                     </div>
                                     <p className="text-sm text-slate-600 leading-relaxed mb-2">
                                        Focalizzata sul <strong>"Qui e Ora"</strong>: non si scava nel passato come causa, ma si osserva come esso influenza il presente. 
                                     </p>
                                     <p className="text-xs text-slate-500 mb-2 italic">"Consapevolezza è il termine chiave: la capacità di prestare attenzione a ciò che succede in me e fuori di me in questo istante."</p>
                                     <ul className="text-xs text-slate-500 space-y-1 list-disc list-inside bg-purple-50/50 p-3 rounded-xl border border-purple-50">
                                         <li><strong>Il Metodo Fenomenologico:</strong> Descrivere ciò che si sente invece di spiegarlo.</li>
                                         <li><strong>Chiusura delle Gestalt:</strong> Completare i "cicli di contatto" rimasti aperti o interrotti nel tempo.</li>
                                         <li><strong>Responsabilità:</strong> Riportare il potere di scelta nelle mani dell'individuo.</li>
                                     </ul>
                                 </div>
                                 <div className="p-5 rounded-2xl border border-slate-100 bg-white shadow-sm hover:border-indigo-100 transition">
                                     <div className="flex items-center gap-3 mb-2">
                                         <div className="w-8 h-8 bg-emerald-100 rounded-lg flex items-center justify-center text-lg">⚡</div>
                                         <h4 className="font-bold text-slate-800">Analisi Bioenergetica (A. Lowen)</h4>
                                     </div>
                                     <p className="text-sm text-slate-600 leading-relaxed mb-2">
                                        Introduce il concetto di <strong>corazza muscolare</strong>: ogni trauma si traduce in una tensione fisica cronica.
                                     </p>
                                     <ul className="text-xs text-slate-500 space-y-1 list-disc list-inside bg-emerald-50/50 p-3 rounded-xl border border-emerald-50">
                                         <li><strong>Grounding:</strong> Il radicamento al suole per recuperare stabilità emotiva.</li>
                                         <li><strong>Aliveness (Vitalità):</strong> Permettere all'energia vitale di fluire attraverso il respiro e il movimento.</li>
                                         <li><strong>Scioglimento delle tensioni:</strong> Utilizzare esercizi corporei per liberare le emozioni trattenute.</li>
                                     </ul>
                                 </div>
                                 <div className="p-5 rounded-2xl border border-slate-100 bg-white shadow-sm hover:border-indigo-100 transition">
                                     <div className="flex items-center gap-3 mb-2">
                                         <div className="w-8 h-8 bg-amber-100 rounded-lg flex items-center justify-center text-lg">⚖️</div>
                                         <h4 className="font-bold text-slate-800">Analisi Transazionale (E. Berne)</h4>
                                     </div>
                                     <p className="text-sm text-slate-600 leading-relaxed mb-2">
                                        Studia le interazioni attraverso gli <strong>"Stati dell'Io"</strong> (Genitore, Adulto, Bambino).
                                     </p>
                                      <ul className="text-xs text-slate-500 space-y-1 list-disc list-inside bg-amber-50/50 p-3 rounded-xl border border-amber-50">
                                         <li><strong>Contratto:</strong> Definire accordi chiari e paritetici per il lavoro comune.</li>
                                         <li><strong>Copione:</strong> Identificare gli "schemi di vita" che tendiamo a ripetere inconsciamente.</li>
                                         <li><strong>Carezze:</strong> Comprendere il bisogno umano di riconoscimento e nutrimento relazionale.</li>
                                     </ul>
                                 </div>
                             </div>
                        </section>

                        <section className="mb-10 p-6 bg-slate-50 rounded-3xl border border-slate-100">
                             <h3 className="text-xl font-bold text-[#24485d] mb-6 flex items-center gap-2 italic">
                                <span className="bg-[#335d73] text-white w-6 h-6 rounded-full flex items-center justify-center text-[10px] not-italic">3</span>
                                Gli Strumenti Creativi & Il Respiro
                             </h3>
                             <p className="text-sm text-slate-600 mb-6">Mediatori utilizzati come "ponti" verso l'inconscio. <strong>Clicca su uno strumento</strong> per approfondire come lo useremo.</p>
                             
                             <div className="grid md:grid-cols-2 gap-4">
                                 {[
                                     { id: 'writing', icon: '✍️', title: 'Scrittura Riflessiva', desc: 'Utilizzata per oggettivare il sé: il diario di bordo e la "lettera mai spedita" permettono di esprimere vissuti profondi e chiudere cicli emotivi sospesi.', details: 'Nelle sedute useremo la scrittura per "far uscire" i pensieri. Mettere su carta aiuta a vedere le cose da una giusta distanza.' },
                                     { id: 'breath', icon: '🫁', title: 'Il Respiro Consapevole', desc: 'Lo strumento centrale: tecniche di respirazione per abbassare l\'ansia o ricaricare il sistema energetico quando spento. Aiuta a sentire il corpo "vivo" e presente.', details: 'Il respiro è il ponte tra mente e corpo. Apprenderai come calibrarlo per gestire i momenti di stress o riattivare la tua energia vitale.' },
                                     { id: 'art', icon: '🎨', title: 'Mediazione Artistica', desc: 'Colori, argilla e collage per dare una forma visibile al mondo interno. Non serve per produrre "arte", ma per visualizzare tensioni e risorse.', details: 'Non serve saper disegnare. Il colore e la forma sono canali diretti per l\'inconscio che spesso arriva dove le parole si fermano.' },
                                     { id: 'music', icon: '🎵', title: 'Musica & Suoni', desc: 'Potente attivatore emozionale. L\'ascolto o la produzione sonora aiutano a dar voce a vissuti che non trovano ancora le parole per essere detti.', details: 'La musica vibra con le nostre emozioni. La useremo come evocatore di immagini e ricordi, o per facilitare l\'espressione corporea.' },
                                     { id: 'body', icon: '🚶‍♂️', title: 'Corpo & Movimento', desc: 'Dinamiche corporee (es. "che gesto farebbe la tua tristezza?") per permettere all\'energia bloccata di fluire e sciogliere la corazza muscolare.', details: 'Il corpo non mente. Attraverso gesti simbolici e movimenti consapevoli, daremo voce alla saggezza del tuo organismo.' }
                                 ].map(tool => (
                                     <div 
                                         key={tool.id} 
                                         onClick={() => setSelectedTool(tool)}
                                         className={`bg-white p-5 rounded-2xl border-2 transition-all cursor-pointer ${selectedTool?.id === tool.id ? 'border-brand-500 bg-brand-50/30' : 'border-slate-100 hover:border-brand-200 hover:shadow-md'}`}
                                     >
                                         <div className="flex items-center gap-3 mb-3">
                                             <span className="text-2xl">{tool.icon}</span>
                                             <h4 className={`text-sm font-black uppercase tracking-wider ${tool.id === 'breath' ? 'text-brand-600' : 'text-slate-700'}`}>{tool.title}</h4>
                                         </div>
                                         <p className="text-[11px] text-slate-500 leading-relaxed">{tool.desc}</p>
                                     </div>
                                 ))}
                             </div>

                             {selectedTool && (
                                 <div className="mt-6 p-5 bg-brand-900 text-white rounded-2xl animate-fade-in shadow-xl relative overflow-hidden">
                                     <div className="absolute top-[-10px] right-[-10px] text-6xl opacity-10">{selectedTool.icon}</div>
                                     <button onClick={() => setSelectedTool(null)} className="absolute top-4 right-4 text-white/50 hover:text-white">✕</button>
                                     <h5 className="font-bold text-lg mb-2 flex items-center gap-2">
                                         <span>{selectedTool.icon}</span>
                                         Approfondimento: {selectedTool.title}
                                     </h5>
                                     <p className="text-sm leading-relaxed text-brand-100">{selectedTool.details}</p>
                                     {selectedTool.id === 'breath' && (
                                         <div className="mt-4 p-3 bg-white/10 rounded-xl border border-white/20">
                                             <p className="text-[10px] font-bold uppercase tracking-widest text-brand-200 border-b border-white/10 pb-1 mb-2">Importanza del Respiro</p>
                                             <p className="text-[11px] italic">In BioGestalt®, il respiro non è solo funzione biologica, ma lo strumento creativio primario che permette di "stare" nel qui ed ora con pienezza.</p>
                                         </div>
                                     )}
                                 </div>
                             )}
                         </section>
                    </div>
                </div>
            );
        }

        return (
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm mt-4 relative animate-fade-in">
                <button onClick={() => setView('menu')} className="absolute top-4 right-6 text-sm font-bold text-slate-500 hover:text-slate-800">Torna al menu ✕</button>
                <h3 className="text-xl font-bold text-slate-800 mb-4 border-b pb-2">Documenti e Risorse</h3>
                
                <div className="grid md:grid-cols-2 gap-4">
                    {docs.map(doc => (
                        <div 
                            key={doc.id} 
                            onClick={() => doc.status === 'active' && setSelectedDocId(doc.id)}
                            className={`p-5 border border-slate-100 rounded-2xl transition flex items-start gap-4 ${doc.status === 'active' ? 'hover:bg-indigo-50 hover:border-indigo-200 cursor-pointer' : 'cursor-not-allowed opacity-60'}`}
                        >
                            <span className="text-3xl mt-1">{doc.icon}</span>
                            <div>
                                <h4 className="font-bold text-slate-800">{doc.title}</h4>
                                <p className="text-xs text-slate-500 mt-1">{doc.excerpt}</p>
                                {doc.status === 'active' ? (
                                    <button className="mt-3 text-xs font-black text-indigo-600 uppercase tracking-widest">Apri Documento →</button>
                                ) : (
                                    <span className="mt-3 block text-[9px] font-black text-slate-400 uppercase tracking-widest">Prossimamente disponibile</span>
                                )}
                            </div>
                        </div>
                    ))}
                </div>

                <div className="mt-8 p-4 bg-blue-50/50 rounded-xl border border-blue-100 italic text-xs text-blue-800">
                    Nota: Questi sono documenti standard aggiornati periodicamente dal tuo counselor. Se hai bisogno di una copia firmata del tuo contratto specifico, richiedila durante il prossimo incontro.
                </div>
            </div>
        );
    }

    if (view === 'anagrafica') {
        const handleSaveAnagrafica = async () => {
            setIsSaving(true);
            try {
                await DataManager.updateClient(client.id, {
                    name: formData.name,
                    phone: formData.phone,
                    birthDate: formData.birthDate
                });
                alert('Dati aggiornati con successo!');
            } catch (err) {
                console.error(err);
                alert('Errore durante il salvataggio.');
            }
            setIsSaving(false);
        };

        return (
            <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm mt-4 relative animate-fade-in">
                <button onClick={() => setView('menu')} className="absolute top-4 right-6 text-sm font-bold text-slate-500 hover:text-slate-800">Torna al menu ✕</button>
                <h3 className="text-xl font-bold text-slate-800 mb-4 border-b pb-2">La tua Anagrafica</h3>
                
                <div className="grid md:grid-cols-2 gap-4 max-w-2xl">
                    <div className="bg-slate-50 p-4 rounded-xl">
                        <strong className="block text-xs text-slate-500 uppercase tracking-wide mb-1">Nome Completo</strong>
                        <input 
                            type="text" 
                            className="bg-transparent border-b border-gray-300 w-full text-lg font-medium text-slate-800 focus:outline-none focus:border-brand-500"
                            value={formData.name}
                            onChange={(e) => setFormData({...formData, name: e.target.value})}
                        />
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl">
                        <strong className="block text-xs text-slate-500 uppercase tracking-wide mb-1">Email <span className="lowercase text-[10px]">(non modificabile)</span></strong>
                        <span className="text-lg font-medium text-slate-800 block mt-1">{client.email}</span>
                    </div>
                    <div className="bg-slate-50 p-4 rounded-xl">
                        <strong className="block text-xs text-slate-500 uppercase tracking-wide mb-1">Telefono</strong>
                        <input 
                            type="tel" 
                            placeholder="+39 ..."
                            className="bg-transparent border-b border-gray-300 w-full text-lg font-medium text-slate-800 focus:outline-none focus:border-brand-500"
                            value={formData.phone}
                            onChange={(e) => setFormData({...formData, phone: e.target.value})}
                        />
                    </div>
                     <div className="bg-slate-50 p-4 rounded-xl">
                        <strong className="block text-xs text-slate-500 uppercase tracking-wide mb-1">Data di Nascita</strong>
                        <input 
                            type="date" 
                            className="bg-transparent border-b border-gray-300 w-full text-lg font-medium text-slate-800 focus:outline-none focus:border-brand-500"
                            value={formData.birthDate}
                            onChange={(e) => setFormData({...formData, birthDate: e.target.value})}
                        />
                    </div>
                </div>
                
                <div className="mt-6 flex items-center justify-between max-w-2xl pt-4 border-t border-slate-100">
                    <p className="text-xs text-slate-500">I dati anagrafici sono visibili al tuo counselor sul tuo fascicolo.</p>
                    <button 
                        onClick={handleSaveAnagrafica}
                        disabled={isSaving}
                        className="px-6 py-2 bg-slate-900 text-white font-bold rounded-xl text-sm hover:bg-slate-800 transition disabled:opacity-50"
                    >
                        {isSaving ? 'Salvataggio...' : 'Salva Modifiche'}
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="bg-white rounded-2xl p-6 border border-slate-100 shadow-sm mt-4 animate-fade-in">
            <h3 className="text-lg font-bold text-slate-800 mb-4 border-b pb-2">Cosa desideri fare?</h3>
            <div className="grid md:grid-cols-2 gap-4">
                
                <button onClick={() => setView('new')} className="text-left p-6 rounded-2xl border border-slate-200 hover:border-emerald-300 hover:bg-emerald-50 transition group flex flex-col items-start gap-2">
                    <span className="text-3xl grayscale group-hover:grayscale-0 transition">✍️</span>
                    <strong className="text-lg text-slate-800 font-sans">Scrivi un nuovo questionario</strong>
                    <span className="text-sm text-slate-500">Aggiorna le tue aspettative da zero.</span>
                </button>

                <button onClick={() => setView('list')} className="text-left p-6 rounded-2xl border border-slate-200 hover:border-indigo-300 hover:bg-indigo-50 transition group flex flex-col items-start gap-2">
                    <span className="text-3xl grayscale group-hover:grayscale-0 transition">📂</span>
                    <strong className="text-lg text-slate-800 font-sans">Visualizza o elimina un questionario</strong>
                    <span className="text-sm text-slate-500">Consulta lo storico o cancella dati pregressi.</span>
                </button>

                <button onClick={() => setView('anagrafica')} className="text-left p-6 rounded-2xl border border-slate-200 hover:border-blue-300 hover:bg-blue-50 transition group flex flex-col items-start gap-2">
                    <span className="text-3xl grayscale group-hover:grayscale-0 transition">🆔</span>
                    <strong className="text-lg text-slate-800 font-sans">Consulta l'anagrafica</strong>
                    <span className="text-sm text-slate-500">Rivedi i tuoi dati personali.</span>
                </button>

                <button onClick={() => setView('documents')} className="text-left p-6 rounded-2xl border border-slate-200 hover:border-amber-300 hover:bg-amber-50 transition group flex flex-col items-start gap-2">
                    <span className="text-3xl grayscale group-hover:grayscale-0 transition">📄</span>
                    <strong className="text-lg text-slate-800 font-sans">Documenti</strong>
                    <span className="text-sm text-slate-500">Privacy, contratti e materiale informativo.</span>
                </button>

            </div>
        </div>
    );
};

export default ProfileModuli;
